import { useEffect, useMemo } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import MarpolGridWasteDisposal from "@features/vessel-call/vessel-call-status/PortServices/form/waste-disposal/MarpolGridWasteDisposal";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { usePortServicesFormStore } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useOffshoreServiceCompanies } from "@hooks/useOffshoreServiceCompanies";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type FromToLocation } from "@services/vessel-call/types";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

type SlopsFieldsOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  selectedPortCode?: string;
};

const SLOPS_DOCUMENT_FIELDS: Array<keyof PortServicesFormData> = [
  "attachmentDocuments",
  "slopsDocument",
];

type CompanyType = {
  companyNameEnglish?: string;
  companyCode?: string;
};

const SlopsFields = ({ serviceType, langBase }: { serviceType: string; langBase: string }) => {
  const { formData, updateFormData, selectedPortCode } = useOutletContext<SlopsFieldsOutlet>();
  const { user } = useAuthStore();
  const { serviceCategoryCode, serviceTypeCode, serviceSubTypeCode } = usePortServicesFormStore();
  const { t } = useTranslation();
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const { options: companies } = useFormattedReference<CompanyType>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const { options: locations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-vessel-call",
  );

  const { options: offshoreCompanies = [] } = useOffshoreServiceCompanies(
    {
      portServiceCategoryCode: serviceCategoryCode,
      portServiceTypeCode: serviceTypeCode,
      portServiceSubtypeCode: serviceSubTypeCode,
    },
    "bff-vessel",
  );

  const serviceProviderOptions = useMemo(
    () =>
      offshoreCompanies.map(({ companyNameEnglish, companyCode }) => ({
        value: companyNameEnglish ?? companyCode ?? "",
        label: companyNameEnglish ?? companyCode ?? "",
      })),
    [offshoreCompanies],
  );

  const fromToLocationOptions = useMemo(
    () =>
      locations
        .filter(({ portCode, location }) => portCode === selectedPortCode && location === "ANCH")
        .map(({ fromToLocationCode, locationName }) => ({
          value: fromToLocationCode,
          label: locationName,
        })),
    [locations, selectedPortCode],
  );

  const name = useMemo(
    () => companies.find((c) => c.companyCode === user?.companyId)?.companyNameEnglish ?? "",
    [companies, user?.companyId],
  );

  useEffect(() => {
    formData.setValue("registeredAgentName", "");
    formData.setValue("registeredAgentContactPerson", "");
    formData.setValue("serviceProviderName", "");
    formData.setValue("rts", "");
    formData.setValue("operationLocation", "");
    formData.setValue("intendedOperationDetails", "");
    formData.setValue("wasteResiduesForDischargeFacility", []);
    SLOPS_DOCUMENT_FIELDS.forEach((fieldName) => {
      updateFormData(fieldName, []);
      clearFilesForKey(fieldName);
    });
  }, [serviceType]);

  useEffect(() => {
    formData.setValue("registeredAgentName", name);
    formData.setValue("registeredAgentContactPerson", user?.name ?? "");
  }, [name, user?.name]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${langBase}.fields.title-registered-agent`)}>
        <FormItem<PortServicesFormData, "registeredAgentName">
          name="registeredAgentName"
          type="text"
          label={t(`${langBase}.fields.name`)}
          testId="portServices-registeredAgentName"
          onFieldChange={updateFormData}
        />
        <FormItem<PortServicesFormData, "registeredAgentContactPerson">
          name="registeredAgentContactPerson"
          type="text"
          label={t(`${langBase}.fields.contact-person`)}
          testId="portServices-registeredAgentContactPerson"
          onFieldChange={updateFormData}
        />
      </FormSection>
      <FormSection title={t(`${langBase}.fields.title-intended-operation`)}>
        <div>
          <FormItem<PortServicesFormData, "serviceProviderName">
            name="serviceProviderName"
            type="autocomplete"
            options={serviceProviderOptions}
            label={t(`${langBase}.fields.service-provider-name`)}
            testId="portServices-serviceProviderName"
            onFieldChange={updateFormData}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
          <FormItem<PortServicesFormData, "rts">
            name="rts"
            type="date-picker"
            label={t(`${langBase}.fields.rts`)}
            testId="portServices-rts"
            onFieldChange={updateFormData}
            timePicker
            combinedInput
            dateConstraint="not-past"
          />
          <FormItem<PortServicesFormData, "operationLocation">
            name="operationLocation"
            type="autocomplete"
            options={fromToLocationOptions}
            label={t(`${langBase}.fields.operation-location`)}
            testId="portServices-operationLocation"
            onFieldChange={updateFormData}
          />
        </div>
        <div className="md:col-span-2">
          <FormItem<PortServicesFormData, "intendedOperationDetails">
            name="intendedOperationDetails"
            type="textarea"
            maxChars={2500}
            minRows={3}
            label={t(`${langBase}.fields.details-of-intended-operation`)}
            testId="portServices-intendedOperationDetails"
            onFieldChange={updateFormData}
            fullWidth
            showCharCounter
          />
        </div>
        <div className="col-span-full">
          <MarpolGridWasteDisposal />
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default SlopsFields;
